<template>
  <div class="recipe-editor">
    <div class="editor-header">
      <h2>{{ isNew ? 'Create New Recipe' : 'Edit Recipe' }}</h2>
      <div class="header-actions">
        <button type="button" @click="confirmClose" class="btn-secondary">
          <fa icon="times" /> Cancel
        </button>
        <button @click="save" class="btn-primary" :disabled="uploading || !canSave || !hasUnsavedChanges">
          <fa icon="save" /> {{ uploading ? 'Uploading...' : 'Save Recipe' }}
        </button>
      </div>
    </div>

    <div class="editor-content" :class="{ 'split-view': isFullscreen }">
        <div class="editor-column left-column">
          <!-- Basic Info Card -->
          <div class="editor-card">
            <h3 class="card-title">Basic Information</h3>
            <div class="form-grid">
              <div class="form-group span-3">
                <label>Recipe Title *</label>
                <input 
                  v-model="recipe.Title" 
                  required 
                  @input="generateUrl"
                  placeholder="e.g. Chocolate Chip Cookies"
                  class="form-input"
                  tabindex="1"
                  ref="titleInput"
                />
              </div>

              <div class="form-group">
                <label>Cooking Time (minutes)</label>
                <input 
                  v-model="recipe.CookingTime" 
                  type="number"
                  min="0"
                  placeholder="e.g. 30"
                  class="form-input"
                  tabindex="2"
                />
              </div>

              <div class="form-group">
                <label>URL Slug *</label>
                <div class="url-preview">
                  <span class="url-prefix">/opskrift/</span>
                  <input 
                    v-model="recipe.Url" 
                    required
                    placeholder="chocolate-chip-cookies"
                    class="form-input url-input"
                    tabindex="-1"
                  />
                </div>
              </div>

              <div class="form-group">
                <label>Recipe ID</label>
                <input 
                  v-model="recipe.Id" 
                  readonly
                  class="form-input readonly"
                  tabindex="-1"
                />
              </div>

              <!-- Description/Intro -->
              <div class="form-group span-3">
                <label>Recipe Intro</label>
                <div class="instructions-editor">
                  <div class="editor-toolbar">
                    <small class="helper-text">
                      <fa icon="info-circle" /> 
                      Brief introduction or description. Supports bold and italic formatting.
                    </small>
                    <div class="format-buttons">
                      <button type="button" @click="formatDescription('bold')" class="format-btn" title="Bold">
                        <fa icon="bold" />
                      </button>
                      <button type="button" @click="formatDescription('italic')" class="format-btn" title="Italic">
                        <fa icon="italic" />
                      </button>
                    </div>
                  </div>
                  <div 
                    ref="descriptionEditor"
                    @input="updateDescription"
                    contenteditable="true"
                    class="wysiwyg-editor"
                    placeholder="Enter a brief introduction or description for this recipe..."
                    tabindex="3"
                  ></div>
                </div>
              </div>

              <!-- Source Link -->
              <div class="form-group span-3">
                <label>Source Link</label>
                <input 
                  v-model="recipe.SourceLink" 
                  type="url"
                  placeholder="https://example.com/original-recipe"
                  class="form-input"
                  tabindex="4"
                />
              </div>

            </div>
          </div>

          <!-- Image Upload Card -->
          <div class="editor-card">
            <h3 class="card-title">Recipe Image</h3>
            <div class="image-upload-section">
              <div class="image-preview-area">
                <div
                  v-if="recipe.ImageUrl || previewImage"
                  class="image-preview focal-target"
                  ref="focalTarget"
                  @click="setFocalFromEvent"
                >
                  <img
                    :src="previewImage || computedImageUrl"
                    alt="Recipe Preview"
                    draggable="false"
                  />
                  <div
                    class="focal-marker"
                    :style="{ left: focalPoint.x + '%', top: focalPoint.y + '%' }"
                    @mousedown.prevent.stop="startFocalDrag"
                    @touchstart.prevent.stop="startFocalDrag"
                    title="Træk for at vælge fokuspunkt"
                  >
                    <span class="focal-marker-ring"></span>
                    <span class="focal-marker-dot"></span>
                  </div>
                  <button
                    type="button"
                    @click.stop="clearImage"
                    class="remove-image"
                    v-if="!uploading"
                  >
                    <fa icon="times" />
                  </button>
                </div>
                <div v-else class="image-placeholder">
                  <fa icon="image" />
                  <span>No image uploaded</span>
                </div>
              </div>

              <div class="upload-controls">
                <input
                  type="file"
                  @change="handleFileUpload"
                  accept="image/*"
                  ref="fileInput"
                  style="display: none;"
                  tabindex="-1"
                />
                <button
                  type="button"
                  @click="$refs.fileInput.click()"
                  class="upload-btn"
                  :disabled="uploading"
                  tabindex="5"
                >
                  <fa :icon="uploading ? 'spinner' : 'upload'" :spin="uploading" />
                  {{ uploading ? `Uploading ${uploadProgress}%` : 'Choose Image' }}
                </button>
                <small class="helper-text">Recommended: 800x600px or larger</small>

                <div v-if="recipe.ImageUrl || previewImage" class="focal-block">
                  <div class="focal-block-header">
                    <span class="focal-block-label">Forhåndsvisning</span>
                    <button
                      v-if="hasCustomFocal"
                      type="button"
                      class="focal-reset"
                      @click="resetFocalPoint"
                      title="Tilbage til midten"
                    >Nulstil</button>
                  </div>
                  <div
                    class="focal-preview-frame"
                    :style="{
                      backgroundImage: `url(${previewImage || computedImageUrl})`,
                      backgroundPosition: `${focalPoint.x}% ${focalPoint.y}%`
                    }"
                  ></div>
                  <small class="focal-block-hint">
                    <fa icon="crosshairs" /> Klik på billedet for at flytte fokuspunktet
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Categories Card -->
          <div class="editor-card">
            <h3 class="card-title">Categories</h3>
            <CategorySelector 
              v-model="recipe.Categories" 
              :recipeContent="{ title: recipe.Title, instructions: rawInstructions }"
            />
          </div>
        </div>

        <div class="editor-column right-column">
          <!-- Ingredients Card -->
          <div class="editor-card">
            <h3 class="card-title">Ingredients</h3>
            <div class="ingredients-editor">
              <div class="editor-toolbar">
              <small class="helper-text">
                <fa icon="info-circle" /> 
                Write each ingredient on a new line. Use bold for section headers (e.g., TOPPING, DOUGH).
              </small>
              <div class="format-buttons">
                <button type="button" @click="formatIngredients('bold')" class="format-btn" title="Bold">
                  <fa icon="bold" />
                </button>
                <button type="button" @click="formatIngredients('italic')" class="format-btn" title="Italic">
                  <fa icon="italic" />
                </button>
              </div>
            </div>
            <div 
              ref="ingredientsEditor"
              @input="updateIngredients"
              contenteditable="true"
              class="wysiwyg-editor"
              placeholder="One ingredient per line. Use bold for section headers like DOUGH or TOPPING..."
              tabindex="7"
            ></div>
            </div>
          </div>

          <!-- Instructions Card -->
          <div class="editor-card">
            <h3 class="card-title">Instructions</h3>
            <div class="instructions-editor">
              <div class="editor-toolbar">
                <small class="helper-text">
                  <fa icon="info-circle" />
                  Each line = one step. Use the list buttons to add bullets inside a step.
                </small>
                <div class="format-buttons">
                  <button type="button" @click="formatText('bold')" class="format-btn" title="Bold">
                    <fa icon="bold" />
                  </button>
                  <button type="button" @click="formatText('italic')" class="format-btn" title="Italic">
                    <fa icon="italic" />
                  </button>
                  <button type="button" @click="formatText('insertUnorderedList')" class="format-btn" title="Bullet list">
                    <fa icon="list-ul" />
                  </button>
                  <button type="button" @click="formatText('insertOrderedList')" class="format-btn" title="Numbered list">
                    <fa icon="list-ol" />
                  </button>
                </div>
              </div>
              <div 
                ref="instructionsEditor"
                @input="updateInstructions"
                contenteditable="true"
                class="wysiwyg-editor"
                placeholder="New line creates a new instruction on the recipe page when viewed."
                tabindex="8"
              ></div>
            </div>
          </div>

          <!-- Notes Card -->
          <div class="editor-card">
            <h3 class="card-title">Notes</h3>
            <div class="instructions-editor">
              <div class="editor-toolbar">
                <small class="helper-text">
                  <fa icon="info-circle" />
                  One note per line. Use the list buttons to add bullets inside a note.
                </small>
                <div class="format-buttons">
                  <button type="button" @click="formatNotes('bold')" class="format-btn" title="Bold">
                    <fa icon="bold" />
                  </button>
                  <button type="button" @click="formatNotes('italic')" class="format-btn" title="Italic">
                    <fa icon="italic" />
                  </button>
                  <button type="button" @click="formatNotes('insertUnorderedList')" class="format-btn" title="Bullet list">
                    <fa icon="list-ul" />
                  </button>
                  <button type="button" @click="formatNotes('insertOrderedList')" class="format-btn" title="Numbered list">
                    <fa icon="list-ol" />
                  </button>
                </div>
              </div>
              <div
                ref="notesEditor"
                @input="updateNotes"
                contenteditable="true"
                class="wysiwyg-editor"
                placeholder="One note per line. e.g. 'Smager bedre dagen efter' or 'Brug rigeligt salt'..."
                tabindex="9"
              ></div>
            </div>
          </div>

          <!-- Recipe Status Card -->
          <div class="editor-card">
            <h3 class="card-title">Recipe Status</h3>
            <div class="status-toggles">
              <label class="toggle-label">
                <input type="checkbox" v-model="recipe.Favorite" class="toggle-checkbox" />
                <span class="toggle-switch"></span>
                <span class="toggle-text">Mark as Favorite</span>
              </label>
              <label class="toggle-label">
                <input type="checkbox" v-model="recipe.Done" class="toggle-checkbox" />
                <span class="toggle-switch"></span>
                <span class="toggle-text">Mark as Done</span>
              </label>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import CategorySelector from './CategorySelector';
import { mapGetters } from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/storage';
import imageHelper from '@/helpers/imageHelper';

export default {
    name: 'RecipeEditor',
    components: { CategorySelector },
    props: {
        recipeToEdit: Object,
        isFullscreen: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            recipe: {
                Id: '',
                Title: '',
                Url: '',
                ImageUrl: '',
                ImageFocalPoint: null, // { x, y } as percentages, null = default center
                Description: '',
                SourceLink: '',
                Categories: [],
                IngredientsList: [],
                Ingredients: '',
                RecipeInstructions: '',
                Notes: '',
                Favorite: false,
                Done: false,
                CookingTime: '',
                Created: new Date().toISOString()
            },
            rawIngredients: '',
            rawInstructions: '',
            rawDescription: '',
            rawNotes: '',
            uploading: false,
            uploadProgress: 0,
            previewImage: null,
            originalRecipe: null,
            originalIngredients: '',
            originalInstructions: '',
            originalDescription: '',
            originalNotes: ''
        }
    },
    computed: {
        ...mapGetters(['allRecipes']),
        isNew() {
            return !this.recipeToEdit;
        },
        computedImageUrl() {
            return imageHelper.imageUrl(this.recipe.ImageUrl);
        },
        canSave() {
            return this.recipe.Title && this.recipe.Url;
        },
        focalPoint() {
            const f = this.recipe.ImageFocalPoint;
            if (f && typeof f.x === 'number' && typeof f.y === 'number') return f;
            return { x: 50, y: 50 };
        },
        hasCustomFocal() {
            const f = this.recipe.ImageFocalPoint;
            return !!(f && (f.x !== 50 || f.y !== 50));
        },
        hasUnsavedChanges() {
            // For new recipes, check if ANY data has been entered (for cancel confirmation)
            if (this.isNew) {
                return !!(
                    this.recipe.Title ||
                    this.rawIngredients.trim() ||
                    this.rawInstructions.trim() ||
                    this.rawDescription.trim() ||
                    this.rawNotes.trim() ||
                    this.recipe.ImageUrl ||
                    this.recipe.Categories.length > 0 ||
                    this.recipe.CookingTime
                );
            }

            // For existing recipes, check if there are changes from original
            if (!this.originalRecipe) return false;

            const recipeChanged = JSON.stringify(this.recipe) !== JSON.stringify(this.originalRecipe);
            const ingredientsChanged = this.rawIngredients !== this.originalIngredients;
            const instructionsChanged = this.rawInstructions !== this.originalInstructions;
            const descriptionChanged = this.rawDescription !== this.originalDescription;
            const notesChanged = this.rawNotes !== this.originalNotes;

            return recipeChanged || ingredientsChanged || instructionsChanged || descriptionChanged || notesChanged;
        }
    },
    watch: {
        hasUnsavedChanges(newValue) {
            // Emit event to parent so it can react to changes
            this.$emit('change-status', newValue);
        },
        canSave(newValue) {
            // Emit event to parent so it can track save validity
            this.$emit('can-save', newValue);
        }
    },
    created() {
        if(this.recipeToEdit) {
            this.recipe = JSON.parse(JSON.stringify(this.recipeToEdit));
            // Convert ingredients array to HTML format for WYSIWYG editor
            if (this.recipe.IngredientsList && this.recipe.IngredientsList.length > 0) {
                this.rawIngredients = this.recipe.IngredientsList.join('<br />');
            } else if (this.recipe.Ingredients) {
                // Fallback to Ingredients string if list doesn't exist
                this.rawIngredients = this.recipe.Ingredients.replace(/\s+/g, '<br />');
            } else {
                this.rawIngredients = '';
            }
            // Store raw HTML for editing
            this.rawInstructions = this.recipe.RecipeInstructions;
            this.rawDescription = this.recipe.Description || '';
            // Notes is now a single HTML string. Legacy recipes may have it
            // as an array - join them with <br /> for editing so old data
            // still loads correctly.
            if (Array.isArray(this.recipe.Notes)) {
                this.rawNotes = this.recipe.Notes.join('<br />');
            } else {
                this.rawNotes = this.recipe.Notes || '';
            }
        } else {
             // Generate sequential ID
             if(this.allRecipes && this.allRecipes.length > 0) {
                 const ids = this.allRecipes.map(r => parseInt(r.Id)).filter(id => !isNaN(id));
                 const maxId = Math.max(...ids);
                 this.recipe.Id = (maxId + 1).toString(); 
             } else {
                 this.recipe.Id = "1";
             }
        }
    },
    mounted() {
        // Set initial content for WYSIWYG editors
        if (this.$refs.instructionsEditor) {
            this.$refs.instructionsEditor.innerHTML = this.rawInstructions;
        }
        if (this.$refs.descriptionEditor) {
            this.$refs.descriptionEditor.innerHTML = this.rawDescription;
        }
        if (this.$refs.ingredientsEditor) {
            this.$refs.ingredientsEditor.innerHTML = this.rawIngredients;
        }
        if (this.$refs.notesEditor) {
            this.$refs.notesEditor.innerHTML = this.rawNotes;
        }

        // Configure contenteditable to use p tags instead of divs
        document.execCommand('defaultParagraphSeparator', false, 'p');

        // Store original state for unsaved changes detection
        this.originalRecipe = JSON.parse(JSON.stringify(this.recipe));
        this.originalIngredients = this.rawIngredients;
        this.originalInstructions = this.rawInstructions;
        this.originalDescription = this.rawDescription;
        this.originalNotes = this.rawNotes;
        
        // Emit initial change status to parent (important for new recipes)
        this.$nextTick(() => {
            this.$emit('change-status', this.hasUnsavedChanges);
            this.$emit('can-save', this.canSave);
        });
        
        // Add keyboard event listener
        window.addEventListener('keydown', this.handleKeyPress);
        
        // Add paste event listeners to all contenteditable fields
        if (this.$refs.instructionsEditor) {
            this.$refs.instructionsEditor.addEventListener('paste', this.handlePaste);
            this.$refs.instructionsEditor.addEventListener('keydown', this.handleBulletShortcut);
        }
        if (this.$refs.descriptionEditor) {
            this.$refs.descriptionEditor.addEventListener('paste', this.handlePaste);
        }
        if (this.$refs.ingredientsEditor) {
            this.$refs.ingredientsEditor.addEventListener('paste', this.handlePaste);
        }
        if (this.$refs.notesEditor) {
            this.$refs.notesEditor.addEventListener('paste', this.handlePaste);
            this.$refs.notesEditor.addEventListener('keydown', this.handleBulletShortcut);
        }
        
        // Auto-focus and select title field
        if (this.$refs.titleInput) {
            this.$nextTick(() => {
                this.$refs.titleInput.focus();
                this.$refs.titleInput.select();
            });
        }
    },
    beforeDestroy() {
        // Remove keyboard event listener
        window.removeEventListener('keydown', this.handleKeyPress);
        
        // Remove paste event listeners
        if (this.$refs.instructionsEditor) {
            this.$refs.instructionsEditor.removeEventListener('paste', this.handlePaste);
            this.$refs.instructionsEditor.removeEventListener('keydown', this.handleBulletShortcut);
        }
        if (this.$refs.descriptionEditor) {
            this.$refs.descriptionEditor.removeEventListener('paste', this.handlePaste);
        }
        if (this.$refs.ingredientsEditor) {
            this.$refs.ingredientsEditor.removeEventListener('paste', this.handlePaste);
        }
        if (this.$refs.notesEditor) {
            this.$refs.notesEditor.removeEventListener('paste', this.handlePaste);
            this.$refs.notesEditor.removeEventListener('keydown', this.handleBulletShortcut);
        }
    },
    methods: {
        handleBulletShortcut(event) {
            // Markdown-style shortcut: typing "- " at the start of a line
            // converts THAT line to a bullet list item. We do this with manual
            // DOM surgery rather than execCommand('insertUnorderedList') -
            // execCommand wraps the whole "paragraph" the browser considers
            // current, which in a <br>-only editor is the entire field. The
            // visible symptom was a bullet eating the lines above.
            if (event.key !== ' ' || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;

            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            if (!range.collapsed) return;

            const node = range.startContainer;
            if (node.nodeType !== Node.TEXT_NODE) return;
            if (range.startOffset !== 1 || node.textContent[0] !== '-') return;

            // Don't trigger while already inside a list - that's a user trying
            // to type a literal "- " inside a bullet item.
            let ancestor = node.parentElement;
            while (ancestor) {
                if (ancestor.tagName === 'UL' || ancestor.tagName === 'OL') return;
                ancestor = ancestor.parentElement;
            }

            // Confirm "-" sits at the start of a line: previousSibling is null
            // (start of editor) or a <br> (line break from Shift+Enter / Enter).
            const prev = node.previousSibling;
            const atLineStart = prev === null || prev.nodeName === 'BR';
            if (!atLineStart) return;

            event.preventDefault();

            const editor = event.currentTarget;
            const parent = node.parentNode;

            // Gather the nodes that belong to this line: from the "-" text node
            // forward to (but not including) the next <br> or end of parent.
            const lineNodes = [];
            let cur = node;
            while (cur && cur.nodeName !== 'BR') {
                lineNodes.push(cur);
                cur = cur.nextSibling;
            }
            const trailingBr = cur; // the <br> after the line, or null

            // Strip the leading "-" off the first line node.
            node.textContent = node.textContent.substring(1);

            // Build the new <ul><li> structure.
            const ul = document.createElement('ul');
            const li = document.createElement('li');
            ul.appendChild(li);

            // Move the line's content into the <li>, dropping empty text nodes
            // (the one that just had its "-" stripped, if it had nothing else).
            lineNodes.forEach(n => {
                if (n.nodeType === Node.TEXT_NODE && n.textContent === '') return;
                li.appendChild(n);
            });

            // contenteditable needs *something* inside an empty <li> for the
            // caret to render - the standard trick is a placeholder <br>.
            const liNeedsPlaceholder = li.childNodes.length === 0;
            if (liNeedsPlaceholder) li.appendChild(document.createElement('br'));

            // Place the <ul> where the line used to live. If a <br> preceded
            // the line, replace it (the <ul> creates its own block break);
            // otherwise insert at the start of the parent.
            if (prev && prev.nodeName === 'BR') {
                parent.insertBefore(ul, prev);
                prev.remove();
            } else {
                parent.insertBefore(ul, parent.firstChild);
            }

            // Likewise drop the trailing <br>.
            if (trailingBr && trailingBr.parentNode) trailingBr.remove();

            // Drop the caret inside the new <li>.
            const newRange = document.createRange();
            const firstChild = li.firstChild;
            if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
                newRange.setStart(firstChild, 0);
                newRange.setEnd(firstChild, 0);
            } else {
                newRange.setStart(li, 0);
                newRange.setEnd(li, 0);
            }
            sel.removeAllRanges();
            sel.addRange(newRange);

            // Programmatic DOM changes don't fire input events on their own;
            // dispatch one so Vue's v-on:input watcher updates rawNotes /
            // rawInstructions.
            if (editor) editor.dispatchEvent(new Event('input', { bubbles: true }));
        },
        handlePaste(event) {
            // Prevent default paste behavior
            event.preventDefault();
            
            // Get pasted content as HTML
            const pastedData = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain');
            
            // Create a temporary div to parse the pasted HTML
            const temp = document.createElement('div');
            temp.innerHTML = pastedData;
            
            // Replace headings with their text content followed by double br
            const headings = temp.querySelectorAll('h1, h2, h3, h4, h5, h6');
            headings.forEach(heading => {
                const textNode = document.createTextNode(heading.textContent);
                const br1 = document.createElement('br');
                const br2 = document.createElement('br');
                heading.parentNode.insertBefore(textNode, heading);
                heading.parentNode.insertBefore(br1, heading);
                heading.parentNode.insertBefore(br2, heading);
                heading.remove();
            });
            
            // Keep emphasis, lists, and line breaks. Everything else (divs,
            // spans, headings, fonts) gets unwrapped to plain text. We
            // intentionally preserve <ul>/<ol>/<li> so pasted bulleted lists
            // survive into instructions and notes. The save-time logic for
            // ingredients flattens these back to lines because that field
            // splits per <br />.
            const KEEP = ['b', 'strong', 'i', 'em', 'br', 'ul', 'ol', 'li'];
            const allElements = temp.querySelectorAll('*');
            allElements.forEach(el => {
                const tagName = el.tagName.toLowerCase();
                if (KEEP.includes(tagName)) {
                    while (el.attributes.length > 0) {
                        el.removeAttribute(el.attributes[0].name);
                    }
                } else {
                    while (el.firstChild) {
                        el.parentNode.insertBefore(el.firstChild, el);
                    }
                    el.remove();
                }
            });
            
            // Normalize the HTML (convert strong to b, em to i)
            let cleanedHTML = temp.innerHTML
                .replace(/<strong>/gi, '<b>')
                .replace(/<\/strong>/gi, '</b>')
                .replace(/<em>/gi, '<i>')
                .replace(/<\/em>/gi, '</i>')
                .replace(/&nbsp;/g, ' ');
            
            // Insert the cleaned HTML at cursor position
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                
                // Create a fragment from the cleaned HTML
                const fragment = range.createContextualFragment(cleanedHTML);
                range.insertNode(fragment);
                
                // Move cursor to end of inserted content
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        },
        generateUrl() {
            if(this.isNew && this.recipe.Title) {
                this.recipe.Url = this.recipe.Title
                    .toLowerCase()
                    .replace(/[æ]/g, 'ae')
                    .replace(/[ø]/g, 'oe')
                    .replace(/[å]/g, 'aa')
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '');
            }
        },
        formatText(command) {
            document.execCommand(command, false, null);
            this.$refs.instructionsEditor.focus();
        },
        formatDescription(command) {
            document.execCommand(command, false, null);
            this.$refs.descriptionEditor.focus();
        },
        formatIngredients(command) {
            document.execCommand(command, false, null);
            this.$refs.ingredientsEditor.focus();
        },
        formatNotes(command) {
            document.execCommand(command, false, null);
            this.$refs.notesEditor.focus();
        },
        updateInstructions() {
            this.rawInstructions = this.$refs.instructionsEditor.innerHTML;
        },
        updateDescription() {
            this.rawDescription = this.$refs.descriptionEditor.innerHTML;
        },
        updateIngredients() {
            this.rawIngredients = this.$refs.ingredientsEditor.innerHTML;
        },
        updateNotes() {
            this.rawNotes = this.$refs.notesEditor.innerHTML;
        },
        flattenListsToLines(html) {
            // Turn <ul><li>a</li><li>b</li></ul> into a<br />b<br />.
            // Used by the ingredients save path - that field is a line-based
            // array, so list structure has to be expanded back into lines.
            const temp = document.createElement('div');
            temp.innerHTML = html;
            temp.querySelectorAll('li').forEach(li => {
                const html = li.innerHTML;
                const wrapper = document.createElement('span');
                wrapper.innerHTML = html;
                const br = document.createElement('br');
                while (wrapper.firstChild) {
                    li.parentNode.insertBefore(wrapper.firstChild, li);
                }
                li.parentNode.insertBefore(br, li);
                li.remove();
            });
            temp.querySelectorAll('ul, ol').forEach(list => {
                while (list.firstChild) list.parentNode.insertBefore(list.firstChild, list);
                list.remove();
            });
            return temp.innerHTML;
        },
        cleanHTML(html) {
            // Create a temporary div to parse HTML
            const temp = document.createElement('div');
            temp.innerHTML = html;

            // First pass: remove style attributes and unwanted attributes
            const allElements = temp.querySelectorAll('*');
            allElements.forEach(el => {
                el.removeAttribute('style');
                el.removeAttribute('class');
                el.removeAttribute('data-rte-list');
            });

            // Second pass: unwrap span and font tags while preserving structure
            const spans = temp.querySelectorAll('span, font');
            spans.forEach(el => {
                const parent = el.parentNode;
                if (!parent) return;
                
                // Move all children out of the span/font
                while (el.firstChild) {
                    parent.insertBefore(el.firstChild, el);
                }
                // Remove the now-empty span/font
                parent.removeChild(el);
            });

            // Get cleaned HTML
            let cleaned = temp.innerHTML;
            
            // Remove HTML comments (like <!--StartFragment-->)
            cleaned = cleaned.replace(/<!--[\s\S]*?-->/g, '');
            
            // Convert paragraph structure to br tags
            // Step-by-step approach to handle all edge cases
            cleaned = cleaned
                // 1. Normalize br tags to <br />
                .replace(/<br\s*\/?>/gi, '<br />')
                // 2. Clean up nbsp first
                .replace(/&nbsp;/g, ' ')
                // 3. Replace paragraph breaks (</p><p>) with double br for visual separation
                .replace(/<\/p>\s*<p>/gi, '<br /><br />')
                // 4. Replace opening <p> that follows content (not at start) with br
                .replace(/(\S)\s*<p>/gi, '$1<br />')
                // 5. Remove any remaining opening <p> tags (at start or after cleanup)
                .replace(/<p>/gi, '')
                // 6. Replace closing </p> tags with br
                .replace(/<\/p>/gi, '<br />')
                // 7. Remove empty tags (like <i></i>, <b></b>)
                .replace(/<(b|i|strong|em)>\s*<\/\1>/gi, '')
                // 8. Clean up excessive breaks (more than 2 in a row)
                .replace(/(<br \/>){3,}/gi, '<br /><br />')
                // 9. Remove leading/trailing breaks
                .replace(/^(<br \/>)+/, '')
                .replace(/(<br \/>)+$/, '')
                .trim();
            
            return cleaned;
        },

        clearImage() {
            this.recipe.ImageUrl = '';
            this.previewImage = null;
            this.recipe.ImageFocalPoint = null;
        },
        setFocalFromEvent(event) {
            // Ignore clicks that already came from the marker (it stops propagation).
            const target = this.$refs.focalTarget;
            if (!target) return;
            const rect = target.getBoundingClientRect();
            const point = this.eventToPoint(event, rect);
            this.updateFocalPoint(point.x, point.y);
        },
        startFocalDrag(event) {
            this.draggingFocal = true;
            const move = (e) => {
                if (!this.draggingFocal) return;
                const target = this.$refs.focalTarget;
                if (!target) return;
                const rect = target.getBoundingClientRect();
                const point = this.eventToPoint(e, rect);
                this.updateFocalPoint(point.x, point.y);
            };
            const up = () => {
                this.draggingFocal = false;
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
                window.removeEventListener('touchmove', move);
                window.removeEventListener('touchend', up);
            };
            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
            window.addEventListener('touchmove', move, { passive: false });
            window.addEventListener('touchend', up);
            // Treat the initial press as a click position too.
            move(event);
        },
        eventToPoint(event, rect) {
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            const clientY = event.touches ? event.touches[0].clientY : event.clientY;
            const x = ((clientX - rect.left) / rect.width) * 100;
            const y = ((clientY - rect.top) / rect.height) * 100;
            return { x, y };
        },
        updateFocalPoint(x, y) {
            const clamp = v => Math.round(Math.max(0, Math.min(100, v)) * 10) / 10;
            this.recipe.ImageFocalPoint = { x: clamp(x), y: clamp(y) };
        },
        resetFocalPoint() {
            this.recipe.ImageFocalPoint = null;
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Show preview immediately
            const reader = new FileReader();
            reader.onload = (e) => {
                this.previewImage = e.target.result;
            };
            reader.readAsDataURL(file);

            this.uploading = true;
            this.uploadProgress = 0;

            // Generate unique filename
            const fileExtension = file.name.split('.').pop();
            const timestamp = Date.now();
            const uniqueFilename = `recipe-${this.recipe.Id}-${timestamp}.${fileExtension}`;

            const storageRef = firebase.storage().ref();
            const fileRef = storageRef.child(uniqueFilename);
            const task = fileRef.put(file);

            task.on('state_changed', 
                (snapshot) => {
                    this.uploadProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                }, 
                (error) => {
                    console.error("Upload error:", error);
                    this.uploading = false;
                    alert("Upload failed! " + error.message);
                    this.previewImage = null;
                }, 
                () => {
                    this.uploading = false;
                    this.recipe.ImageUrl = uniqueFilename;
                    this.previewImage = null; // Clear preview, use actual URL now
                }
            );
        },
        save() {
            if (!this.canSave) return;

            // Get HTML content from ingredients WYSIWYG editor and clean it
            const ingredientsContent = this.$refs.ingredientsEditor.innerHTML;
            // Flatten any lists into <br />-separated lines first. Ingredients
            // are line-based (one per row), so a pasted <ul><li>...</li></ul>
            // would otherwise become a single mega-ingredient.
            const ingredientsFlattened = this.flattenListsToLines(ingredientsContent);
            const cleanedIngredients = this.cleanHTML(ingredientsFlattened);

            // Split by line breaks to create array
            this.recipe.IngredientsList = cleanedIngredients
                .split('<br />')
                .map(line => line.trim())
                .filter(line => line.length > 0); // Remove empty lines

            // Generate Ingredients string from list (for backward compatibility)
            this.recipe.Ingredients = this.recipe.IngredientsList.join(' ');

            // Get HTML content from WYSIWYG editor and clean it
            const editorContent = this.$refs.instructionsEditor.innerHTML;
            const cleanedContent = this.cleanHTML(editorContent);
            
            this.recipe.RecipeInstructions = cleanedContent;

            // Get Description content from WYSIWYG editor and clean it
            const descriptionContent = this.$refs.descriptionEditor.innerHTML;
            const cleanedDescription = this.cleanHTML(descriptionContent);
            this.recipe.Description = cleanedDescription;

            // Notes - one HTML blob rendered inside a single green card on
            // the recipe page. <br /> in the source becomes a paragraph break
            // in the rendered note; bullet lists are preserved as-is.
            const notesContent = this.$refs.notesEditor ? this.$refs.notesEditor.innerHTML : '';
            this.recipe.Notes = this.cleanHTML(notesContent);

            this.$store.dispatch('saveRecipe', this.recipe);
            this.$emit('saved');
        },
        handleKeyPress(event) {
            // F key - toggle fullscreen
            if (event.key === 'f' || event.key === 'F') {
                // Only if not typing in an input field
                if (!['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) && 
                    !document.activeElement.hasAttribute('contenteditable')) {
                    event.preventDefault();
                    this.$emit('toggle-fullscreen');
                }
            }
            
            // ESC key - close with confirmation if unsaved changes
            if (event.key === 'Escape') {
                event.preventDefault();
                this.confirmClose();
            }
        },
        confirmClose() {
            if (this.hasUnsavedChanges) {
                if (confirm('You have unsaved changes. Are you sure you want to close?')) {
                    this.$emit('cancel');
                }
            } else {
                this.$emit('cancel');
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.recipe-editor {
    padding: 20px;
}

.editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid var(--lgray);

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
        gap: 15px;
    }

    h2 {
        color: var(--dark);
        margin: 0;
    }

    .header-actions {
        display: flex;
        gap: 10px;
    }
}

.editor-content {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &.split-view {
        flex-direction: row;
        height: 100%;

        .editor-column {
            flex: 1;
            overflow-y: auto;

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-track {
                background: var(--lgray);
            }

            &::-webkit-scrollbar-thumb {
                background: var(--darker);
                border-radius: 10px;
            }
        }

        .left-column {
            flex: 0 0 calc(50% - 10px);
        }

        .right-column {
            flex: 0 0 calc(50% - 10px);
        }
    }
}

.editor-card {
    background: #fafbfc;
    border: 2px solid #e1e8ed;
    border-radius: 10px;
    padding: 20px;
    box-shadow: var(--cardshadow);
    margin-bottom: 20px;

    .card-title {
        color: var(--darker);
        margin: 0 0 20px 0;
        padding-bottom: 10px;
        border-bottom: 2px solid #d8e5e0;
        font-size: 1.3rem;
        background: linear-gradient(135deg, #e8f5e0 0%, #f0f7ed 100%);
        margin: -20px -20px 20px -20px;
        padding: 15px 20px;
        border-radius: 8px 8px 0 0;
    }
    &:last-of-type {
        margin-bottom: 0;
    }
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }

    .span-2 {
        grid-column: span 2;

        @media (max-width: 768px) {
            grid-column: span 1;
        }
    }

    .span-3 {
        grid-column: span 3;

        @media (max-width: 768px) {
            grid-column: span 1;
        }
    }
}

.form-group {
    display: flex;
    flex-direction: column;

    label {
        font-size: 0.9rem;
        font-weight: 600;
        color: var(--dark);
        margin-bottom: 8px;
    }
}

.form-input,
.form-textarea {
    padding: 12px;
    border: 2px solid var(--lgray);
    border-radius: 6px;
    font-family: 'Poppins';
    font-size: 0.95rem;
    color: var(--darker);
    transition: all 0.2s;
    background: white;

    &:focus {
        outline: none;
        border-color: var(--main);
    }

    &.readonly {
        background: var(--lgray);
        cursor: not-allowed;
    }

    &::placeholder {
        color: #999;
    }
}

.url-preview {
    display: flex;
    align-items: center;
    border: 2px solid var(--lgray);
    border-radius: 6px;
    overflow: hidden;
    transition: all 0.2s;

    &:focus-within {
        border-color: var(--main);
    }

    .url-prefix {
        background: var(--lgray);
        padding: 12px;
        color: #666;
        font-size: 0.9rem;
        font-family: monospace;
        white-space: nowrap;
        height: 48px;
        display: flex;
        align-items: center;
    }

    .url-input {
        border: none;
        flex: 1;
        font-family: monospace;
        padding: 12px;
        height: 48px;
        box-sizing: border-box;

        &:focus {
            border: none;
        }
    }
}

.image-upload-section {
    display: flex;
    gap: 24px;
    align-items: stretch;

    @media (max-width: 768px) {
        flex-direction: column;
    }
}

.image-preview-area {
    flex: 1;
    min-width: 0;
    aspect-ratio: 4 / 3;
    max-height: 360px;
    background: #f8f9fa;
    border-radius: 8px;

    .image-preview {
        position: relative;
        width: 100%;
        height: 100%;
        border-radius: 8px;
        overflow: hidden;

        &.focal-target {
            cursor: crosshair;
            user-select: none;
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            pointer-events: none;
        }

        .remove-image {
            position: absolute;
            top: 10px;
            right: 10px;
            background: rgba(231, 76, 60, 0.9);
            color: white;
            border: none;
            border-radius: 50%;
            width: 32px;
            height: 32px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;

            &:hover {
                background: #c0392b;
                transform: rotate(90deg);
            }
        }
    }

    .image-placeholder {
        width: 100%;
        height: 100%;
        border: 2px dashed var(--lgray);
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #999;
        gap: 10px;

        svg {
            font-size: 3rem;
        }
    }
}

.upload-controls {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    @media (max-width: 768px) {
        width: 100%;
    }
}

.focal-marker {
    position: absolute;
    width: 34px;
    height: 34px;
    transform: translate(-50%, -50%);
    cursor: grab;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;

    &:active { cursor: grabbing; }

    .focal-marker-ring {
        position: absolute;
        inset: 0;
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.4), inset 0 0 0 2px rgba(0, 0, 0, 0.25);
        transition: transform 0.15s ease;
    }

    .focal-marker-dot {
        width: 8px;
        height: 8px;
        background: white;
        border-radius: 50%;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.4);
    }

    &:hover .focal-marker-ring { transform: scale(1.12); }
}

// Focal block: a sub-section inside the upload-controls column. Pushed to
// the bottom of the column (margin-top: auto) so its last line - the hint -
// lines up with the image preview's bottom edge.
.focal-block {
    margin-top: auto;
    padding-top: 14px;
    border-top: 1px solid var(--lgray);
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.focal-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.focal-block-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #999;
    letter-spacing: 0.06em;
}

.focal-reset {
    border: 1px solid var(--main);
    background: white;
    color: var(--darker);
    padding: 4px 12px;
    font-size: 0.72rem;
    font-weight: 600;
    border-radius: 5px;
    cursor: pointer;
    font-family: 'Poppins';
    transition: background 0.15s, color 0.15s;

    &:hover { background: var(--main); color: white; }
}

.focal-preview-frame {
    width: 100%;
    aspect-ratio: 16 / 9;
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 8px;
    border: 1px solid #e1e8ed;
    transition: background-position 0.2s ease;
}

.focal-block-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #888;
    line-height: 1.4;

    svg { color: var(--main); flex-shrink: 0; }
}

.upload-btn {
    background: linear-gradient(135deg, #2c5f2d 0%, #3a7c3f 100%);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 14px 24px;
    font-family: 'Poppins';
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.2s;
    width: 100%;

    &:hover:not(:disabled) {
        background: linear-gradient(135deg, #3a7c3f 0%, #4a8c4f 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(44, 95, 45, 0.3);
    }

    &:focus {
        outline: 3px solid var(--main);
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.ingredients-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ingredient-row {
    display: flex;
    gap: 10px;
    align-items: center;

    .ingredient-number {
        background: var(--main);
        color: white;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 600;
        flex-shrink: 0;
    }

    .form-input {
        flex: 1;
    }
}

.instructions-editor {
    .editor-toolbar {
        margin-bottom: 10px;
    }

    .instructions-textarea {
        font-family: 'Poppins';
        line-height: 1.6;
        resize: vertical;
    }
}

.btn-primary,
.btn-secondary,
.btn-add-item {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-family: 'Poppins';
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
}

.btn-primary {
    background: var(--darker);
    color: white;

    &:hover:not(:disabled) {
        background: var(--dark);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.btn-secondary {
    background: #95a5a6;
    color: white;

    &:hover {
        background: #7f8c8d;
    }
}

.btn-add-item {
    background: var(--lgray);
    color: var(--darker);
    width: 100%;
    justify-content: center;

    &:hover {
        background: var(--main);
        color: white;
    }
}

.btn-icon {
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;

    &.btn-danger {
        background: #e74c3c;
        color: white;

        &:hover {
            background: #c0392b;
        }
    }
}

.helper-text {
    color: #666;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 5px;
}

.full-width-card {
    width: 100%;
}

.ingredients-editor,
.instructions-editor {
    width: 100%;

    .editor-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        flex-wrap: wrap;
        gap: 10px;
    }

    .format-buttons {
        display: flex;
        gap: 5px;
    }

    .format-btn {
        background: var(--lgray);
        border: 1px solid #ddd;
        color: var(--dark);
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 0.9rem;

        &:hover {
            background: var(--main);
            color: white;
            border-color: var(--main);
        }
    }

    .ingredients-textarea,
    .instructions-textarea {
        width: 100%;
        font-family: 'Poppins';
        line-height: 1.6;
        resize: vertical;
    }
}

.form-textarea {
    font-family: 'Poppins';
    line-height: 1.6;
    resize: vertical;
    width: 100%;

    &.description-textarea {
        resize: vertical !important; // Only vertical resize
        width: 100% !important;
    }

    &.ingredients-textarea {
        font-family: monospace;
        font-size: 0.9rem;
    }
}

.status-toggles {
    display: flex;
    gap: 30px;
    flex-wrap: wrap;
    align-items: center;
    padding: 10px 0;
}

.toggle-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
}

.toggle-checkbox {
    display: none;
}

.toggle-switch {
    position: relative;
    width: 44px;
    height: 24px;
    background: #ccc;
    border-radius: 12px;
    transition: background 0.3s;

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        background: white;
        border-radius: 50%;
        transition: transform 0.3s;
    }
}

.toggle-checkbox:checked + .toggle-switch {
    background: var(--main);

    &::after {
        transform: translateX(20px);
    }
}

.toggle-text {
    font-size: 0.9rem;
    color: var(--dark);
    font-weight: 500;
}

.wysiwyg-editor {
    width: 100%;
    min-height: 150px;
    padding: 15px;
    border: 2px solid var(--lgray);
    border-radius: 6px;
    font-family: 'Poppins';
    font-size: 0.95rem;
    line-height: 1.6;
    color: var(--dark);
    background: white;
    overflow-y: auto;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: var(--main);
    }

    &:empty:before {
        content: attr(placeholder);
        color: #999;
        font-style: italic;
    }

    p {
        margin: 0 0 10px 0;
    }

    br + br {
        display: block;
        content: " ";
        margin: 10px 0;
    }

    b, strong { font-weight: 700; }
    i, em     { font-style: italic; }

    // Restore list indent inside the editor. The global "ol, ul { padding-left: 0 }"
    // rule (main.scss) zeroes the default browser padding. ::v-deep is
    // required because lists created by contenteditable (paste, execCommand,
    // or the markdown shortcut) never get Vue's scoped data-v-xxx attribute,
    // so a plain ".wysiwyg-editor ul" selector wouldn't match them.
    ::v-deep ul,
    ::v-deep ol {
        padding-left: 24px;
        margin: 6px 0;
    }
    ::v-deep ul { list-style: disc; }
    ::v-deep ol { list-style: decimal; }
    ::v-deep li {
        margin: 2px 0;
        line-height: 1.55;
    }
}

// Toggle Switch Styles
.toggle-label {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    user-select: none;
    
    .toggle-text {
        font-size: 0.95rem;
        color: var(--dark);
        font-weight: 500;
    }
}

.toggle-checkbox {
    position: absolute;
    opacity: 0;
    
    &:checked + .toggle-switch {
        background: var(--main);
    }
    
    &:checked + .toggle-switch::before {
        transform: translateX(20px);
    }
}

.toggle-switch {
    position: relative;
    width: 44px;
    height: 24px;
    background: #ccc;
    border-radius: 12px;
    transition: background 0.3s;
    flex-shrink: 0;
    
    &::before {
        content: '';
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: white;
        top: 3px;
        left: 3px;
        transition: transform 0.3s;
    }
}

.helper-text {
    color: #999;
    font-size: 0.85rem;
    display: block;
    margin-top: 5px;
}
</style>
